import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import DeleteConfirmationModal from "../../../components/commonComponent/DeleteConfirmationModal";
import Pagination from "../../../components/commonComponent/Pagination";
import Search from "../../../components/commonComponent/Search";
import Table from "../../../components/commonComponent/Table";
import ViewModal from "../../../components/commonComponent/ViewModal";
import useModal from "../../../hooks/useModal";
import "../../../styles/SolarStation.css";
import { deleteData } from "../../../utils/utils";
import useLoader from "../../../hooks/useLoader";
import axios from "axios";
import { generateSlug } from "../../../utils/commonFunc";

const API_URL = process.env.REACT_APP_API_URL;

const columns = [
  { header: "Title", accessor: "title" },
  { header: "Description", accessor: "description" },
];

const ITEMS_PER_PAGE = 10;

const News = () => {
  const [data, setData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [keyword, setKeyword] = useState("solar");

  const [showLangModal, setShowLangModal] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState("en");
  const [pageSize, setPageSize] = useState(10);

  const [previewData, setPreviewData] = useState([]);
  const [selectedNews, setSelectedNews] = useState({});
  const [previewLang, setPreviewLang] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  const { loading, showLoader, hideLoader } = useLoader();
  const navigate = useNavigate();
  const [newsFilter, setNewsFilter] = useState("all");

  const {
    showDeleteModal,
    showViewModal,
    selectedItem,
    handleOpenDeleteModal,
    handleCloseDeleteModal,
    handleOpenViewModal,
    handleCloseViewModal,
  } = useModal();

  const fetchNews = async () => {
    try {
      showLoader();
      const response = await fetch(
        `${API_URL}/news/getAllNews?page=${currentPage}&limit=${ITEMS_PER_PAGE}&search=${searchTerm}`
      );
      const result = await response.json();

      if (response.ok) {
        setData(result.data || []);
        setTotalPages(result.totalPages);
      } else {
        toast.error(result.error || "Failed to fetch news");
      }
    } catch {
      toast.error("Failed to fetch data");
    } finally {
      hideLoader();
    }
  };

  const getFilteredNews = () => {
  let filtered = [...data];

  if (newsFilter === "hindi") {
    filtered = filtered.filter((news) => news.language === "Hindi");
  } else if (newsFilter === "english") {
    filtered = filtered.filter((news) => news.language === "English");
  } else if (newsFilter === "latest") {
    filtered.sort((a, b) => new Date(b.publishedAt) - new Date(a.publishedAt));
  }

  return filtered;
};

const filteredNews = getFilteredNews();


  useEffect(() => {
    fetchNews();
  }, [currentPage, searchTerm]);

  const handleSearch = (term) => {
    if (term.length >= 2 || term.length === 0) {
      setSearchTerm(term);
      setCurrentPage(1);
    }
  };

  const handleDeleteConfirm = () => {
    deleteData(`${API_URL}/news/deleteNewsById`, selectedItem._id)
      .then(() => {
        setData((prev) => prev.filter((item) => item._id !== selectedItem._id));
        toast.success("Deleted successfully");
        fetchNews();
      })
      .catch(() => toast.error("Error deleting data"));
    handleCloseDeleteModal();
  };

  const handleLangModalSubmit = async () => {
    try {
      showLoader();
      const response = await axios.get(`${API_URL}/api/news`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer 2d71a63e177a409c93c93370f8290c37'
        },
        params: {
          q: keyword || 'solar',
          sortBy: 'publishedAt',
          language: selectedLanguage,
          apiKey: '2d71a63e177a409c93c93370f8290c37',
          pageSize,
        }
      });

      const articles = response.data.articles;

      if (articles?.length > 0) {
        setPreviewData(articles);
        setSelectedNews({});
        setPreviewLang(selectedLanguage);
        setShowPreview(true);
        setShowLangModal(false);
      } else {
        toast.error("No articles found to preview");
      }
    } catch {
      toast.error("Failed to fetch preview");
    } finally {
      hideLoader();
    }
  };

 const handleSubmitPreview = async () => {
  const selectedArticles = previewData.filter((_, index) => selectedNews[index]);

  if (selectedArticles.length === 0) {
    toast.warning("Please select at least one article to submit");
    return;
  }

  showLoader();

  try {
    let successCount = 0;

    for (const article of selectedArticles) {
      const newsData = {
        title: article.title || "Untitled",
        image: article.urlToImage || "",
        description: article.description || "No description available",
        content: article.content,
        url: article.url,
        publishedAt: article.publishedAt,
        sourceName: article.source?.name || "",
        sourceUrl: article.url,
        author: article.author || "",
        slug: generateSlug(article.title),
        language: previewLang === 'hi' ? 'Hindi' : 'English',
      };

      try {
        const response = await fetch(`${API_URL}/news/createNewsbyadmin`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(newsData),
        });

        const result = await response.json();

        if (response.ok) {
          successCount++;
        } else {
          if (
            response.status === 500 &&
            result?.error?.includes("duplicate key error")
          ) {
            toast.warning(`Duplicate news skipped: "${article.title}"`);
          } else {
            toast.error(result?.message || `Failed to add: "${article.title}"`);
          }
        }
      } catch (error) {
        toast.error(`Network error for: "${article.title}"`);
        console.error("Error adding article:", error);
      }
    }

    if (successCount > 0) {
      toast.success(
        `${successCount} news ${
          successCount === 1 ? "article" : "articles"
        } added successfully for ${previewLang === "hi" ? "Hindi" : "English"}`
      );
      setShowPreview(false);
      fetchNews();
    }
  } catch (err) {
    toast.error("Something went wrong during submission.");
  } finally {
    hideLoader();
  }
};

  const handleCheckboxChange = (index) => {
    setSelectedNews((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const handleSelectAll = () => {
    const allSelected = {};
    previewData.forEach((_, index) => (allSelected[index] = true));
    setSelectedNews(allSelected);
  };

 const fieldsToShow = {
    Title: "title",
    Description: "description",
  };

  return (
    <div>
      <h2 className="capitalize font-bold text-[24px] mb-4">News List</h2>

    {/* Top Filter + Search Section */}
    <div className="bg-white rounded-md shadow-md px-2 py-2 mb-6 border border-gray-200">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-2">
        {/* Search Input */}
        <div className="w-full md:w-1/3">
          <label className="text-sm font-medium text-gray-700 mb-1 block">Search</label>
          <Search searchFields={["Title", "Description"]} onSearch={handleSearch} />
        </div>

        

        {/* Filter Dropdown */}
        <div className="w-full md:w-1/4">
          <label className="text-sm font-medium text-gray-700 mb-1 block">Filter</label>
          <select
            value={newsFilter}
            onChange={(e) => setNewsFilter(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
          >
            <option value="all">All News</option>
            <option value="hindi">Hindi News</option>
            <option value="english">English News</option>
            <option value="latest">Latest News</option>
          </select>
        </div>


        {/* Keyword Input */}
        <div className="w-full md:w-1/3">
          <label className="text-sm font-medium text-gray-700 mb-1 block">Enter Keyword</label>
          <input
            type="text"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="e.g. solar, energy..."
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
          />
        </div>


        {/* Add News Button */}
        <div className="w-full md:w-auto flex justify-end">
          <button
            onClick={() => setShowLangModal(true)}
            className="mt-2 md:mt-0 px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition text-sm font-medium"
          >
           + News
          </button>
        </div>

      </div>
    </div>

    {/* News Table */}
    <Table
      columns={columns}
      data={filteredNews}
      showIndex={true}
      onDelete={handleOpenDeleteModal}
      onUpdate={(item) => navigate(`/news/edit/${item._id}`)}
      onView={handleOpenViewModal}
      actions={{ showView: true, showDelete: true }}
      loading={loading}
    />

    {/* Pagination */}
    <Pagination
      currentPage={currentPage}
      totalPages={totalPages}
      onPageChange={setCurrentPage}
    />

    {/* Delete Modal */}
    <DeleteConfirmationModal
      show={showDeleteModal}
      onHide={handleCloseDeleteModal}
      onConfirm={handleDeleteConfirm}
    />

    {/* View Modal */}
    <ViewModal
      show={showViewModal}
      onHide={handleCloseViewModal}
      data={selectedItem}
      fields={fieldsToShow}
    />

    {/* Language Modal */}
    {showLangModal && (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center">
        <div className="bg-white p-6 rounded-lg shadow-xl w-[90%] max-w-md relative">
          <h3 className="text-lg font-semibold mb-4">Add News Preferences</h3>

          <label className="block mb-2 font-medium">Select Language:</label>
          <div className="flex gap-6 mb-4">
            <label className="flex items-center gap-2">
              <input type="radio" value="hi" checked={selectedLanguage === 'hi'} onChange={() => setSelectedLanguage("hi")} />
              Hindi
            </label>
            <label className="flex items-center gap-2">
              <input type="radio" value="en" checked={selectedLanguage === 'en'} onChange={() => setSelectedLanguage("en")} />
              English
            </label>
          </div>

          <label className="block mb-2 font-medium">Page Size:</label>
          <input
            type="number"
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
            className="w-full px-3 py-2 mb-4 border rounded-md border-gray-300"
          />

          <div className="flex justify-end gap-3">
            <button
              onClick={() => setShowLangModal(false)}
              className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
            >
              Cancel
            </button>
            <button
              onClick={handleLangModalSubmit}
              className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Submit
            </button>
          </div>
        </div>
      </div>
    )}

    {/* Preview Modal */}
    {showPreview && (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center">
        <div className="bg-white w-[90%] max-w-4xl h-[80%] overflow-y-auto p-6 rounded shadow-lg relative">
          <h3 className="text-xl font-semibold mb-4">
            Preview {previewLang === 'hi' ? "Hindi" : "English"} News
          </h3>

          <button
            onClick={() => setShowPreview(false)}
            className="absolute top-2 right-4 text-gray-500 hover:text-red-500 text-xl"
          >
            &times;
          </button>

          <div className="text-right mb-4">
            <button
              onClick={handleSelectAll}
              className="text-sm px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
            >
              Select All
            </button>
          </div>

          {previewData.map((item, index) => (
            <div
              key={index}
              className="border-b border-gray-300 py-3 flex items-start gap-3"
            >
              <input
                type="checkbox"
                checked={selectedNews[index] || false}
                onChange={() => handleCheckboxChange(index)}
                className="mt-1"
              />
              <div>
                <h4 className="font-bold text-md">{item.title}</h4>
                <p className="text-sm text-gray-600">
                  {item.description || "No description available."}
                </p>
              </div>
            </div>
          ))}

          <div className="mt-4 text-right">
            <button
              onClick={handleSubmitPreview}
              className="px-6 py-2 rounded-md bg-blue-600 text-white font-medium hover:bg-blue-700 transition"
            >
              Submit Selected News
            </button>
          </div>
        </div>
      </div>
    )}
  </div>
);
}

export default News;
